// datumClick.ts
//
// What a click on a bar, a slice or a pivot row hands on (plan 2026-10-05
// dashboard interactivity, B2/B4): the RAW row value of the dimension the
// tile is drawn over, never the label ECharts shows, which may be formatted.
//
// The builder knows which row an ECharts `dataIndex` points at; nobody after
// it does (the rows were capped, re-sorted and trimmed on the way), so the
// builder attaches the datums and the click handler only looks them up:
// `attachChartDatums` writes `verityDatums` (one per data index) and
// `veritySeries` on each series it knows the y_key of, `datumFromClick`
// reads them back by `seriesIndex` / `dataIndex`.
//
// Nothing here runs unless the surface passes an `interaction`; reports and
// chat never do, so their options are unchanged.
import type { EChartsOption } from "echarts";
import type { BackendChartSpec } from "./types";
import type { PivotResult, PivotRow } from "./table/pivotModel";
import { addDays, parseISODate } from "./table/calendar";
import { formatDateValue, isTimeGrain, type TimeGrain } from "./table/formatDateValue";
import { isoKey, nextBucketStart, truncateToGrain } from "./timeAxis";
import { datePartLabellerFor } from "./datePart";

/** Marks the "Other" row `capCategoriesWithOther` appends: a sum of the
 *  dropped rows, not a value any query can filter on. A symbol, so it rides
 *  along with `{...row}` copies and never reaches a serialised payload. */
export const SYNTHETIC_ROW: unique symbol = Symbol.for("verity.chart.syntheticRow");

/** The backend's label for a NULL breakdown value, and the label of its
 *  overflow series (`services/chart_formatter.py`). Neither can be filtered:
 *  SQL `IN` never matches NULL, and "Other" is a bucket. */
const NOT_SET_LABEL = "(not set)";
const OTHER_SERIES_LABEL = "Other";

/** One dimension narrowed to some values. */
export type DatumFilter = { dimension: string; values: string[]; label: string };

/** One time bucket a click on a time value selects (plan 2026-10-10 a click
 *  on a time value): ISO local dates, `end` INCLUSIVE. The dashboard turns it
 *  into a period for the other tiles, never into a dimension filter. */
export type PeriodDatum = { start: string; end: string; grain: TimeGrain; label: string };

/** What a click selects. `filters` apply together (a nested pivot row is its
 *  parent's value and its own); `series` is the split series a bar belongs
 *  to, offered as a filter of its own; `period` is the time bucket of a time
 *  value (then `filters` is empty). */
export type ChartDatum = { filters: DatumFilter[]; series?: DatumFilter; period?: PeriodDatum };

/** Passed by a surface that offers click-to-filter (the dashboard). */
export type DatumInteraction = {
  /** The dimensions this tile may be filtered on from a click: its
   *  `filterable_dimensions`. A dimension outside it is never clickable. */
  clickable: readonly string[];
  /** The filters this tile is the source of, to draw its selection. */
  selected?: Record<string, string[]>;
  /** The surface offers a click on a time value (a period click). */
  periods?: boolean;
  /** The bucket start of the period click this tile is the source of. */
  selectedPeriod?: string;
};

const str = (v: unknown): string => (v === null || v === undefined ? "" : String(v));

function filterable(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  const s = String(value);
  return s !== "" && s !== NOT_SET_LABEL;
}

/** The category datum of each row, in the order the builder draws them, or
 *  null where a click selects nothing: a time axis (a time click is a PERIOD,
 *  `chartRowPeriods`, never a dimension filter), a dimension the tile cannot be filtered on, a null or
 *  "(not set)" value, the synthetic "Other" row. */
export function chartRowDatums(
  spec: BackendChartSpec,
  interaction: DatumInteraction,
  timeIndex: boolean,
): Array<DatumFilter | null> {
  const rows = spec.rows ?? [];
  const key = spec.chart_index_by;
  const clickable = new Set(interaction.clickable);
  if (timeIndex || !key || key === spec.time_dimension_key || !clickable.has(key)) return rows.map(() => null);
  // A date part's chip reads "Oct" while the filter carries "10" (plan
  // 2026-10-16 date parts, S1: a click always carries the raw integer).
  const part = datePartLabellerFor(spec, key);
  return rows.map((row) => {
    if (!row || (row as Record<PropertyKey, unknown>)[SYNTHETIC_ROW]) return null;
    const value = row[key];
    if (!filterable(value)) return null;
    return { dimension: key, values: [String(value)], label: part ? part(value) : String(value) };
  });
}

/** Is the tile drawn over time: a stamped time axis (charts), or an index
 *  that IS the query's time dimension (tables carry no `time_axis`, it is
 *  stamped for chart types only, `services/chart_formatter.py`). */
export function isTimeIndexed(spec: BackendChartSpec, timeAxis: boolean): boolean {
  const key = spec.chart_index_by;
  return timeAxis || (!!key && key === spec.time_dimension_key);
}

/** The bucket a row value falls in at `grain`, or null for an empty or
 *  unparseable value. Parsed as a LOCAL date (`parseISODate`), never
 *  `new Date(iso)`, which is UTC midnight and shifts a day west of UTC. */
export function periodOfValue(value: unknown, grain: TimeGrain): PeriodDatum | null {
  if (value === null || value === undefined) return null;
  const day = parseISODate(String(value).slice(0, 10));
  if (Number.isNaN(day.getTime())) return null;
  const start = truncateToGrain(day, grain);
  const end = addDays(nextBucketStart(start, grain), -1); // nextBucketStart is EXCLUSIVE
  const startIso = isoKey(start);
  return { start: startIso, end: isoKey(end), grain, label: formatDateValue(startIso, grain, "long") };
}

/** The period datum of each row of a time-indexed tile, or null where a
 *  click selects nothing: not time-indexed, no surface offer, an unknown or
 *  sub-daily grain, the synthetic "Other" row, an empty value, a bucket that
 *  starts after the tile's `through` (nothing there to filter to). */
export function chartRowPeriods(
  spec: BackendChartSpec,
  interaction: DatumInteraction,
  timeAxis: boolean,
  grain: unknown,
): Array<PeriodDatum | null> {
  const rows = spec.rows ?? [];
  const key = spec.chart_index_by;
  if (!interaction.periods || !key || !isTimeIndexed(spec, timeAxis) || !isTimeGrain(grain)) {
    return rows.map(() => null);
  }
  const through = typeof spec.time_axis?.through === "string" ? spec.time_axis.through.slice(0, 10) : null;
  return rows.map((row) => {
    if (!row || (row as Record<PropertyKey, unknown>)[SYNTHETIC_ROW]) return null;
    const period = periodOfValue(row[key], grain);
    if (!period || (through && period.start > through)) return null;
    return period;
  });
}

/** The split series a y_key draws, when the tile is split by a clickable
 *  dimension and the key is a real breakdown value. Identified by the y_key,
 *  never by the series name (a display label). */
export function seriesDatum(
  spec: BackendChartSpec,
  interaction: DatumInteraction,
  yKey: string,
): DatumFilter | null {
  const dim = spec.breakdown_key;
  if (!dim || !interaction.clickable.includes(dim)) return null;
  if (!filterable(yKey) || yKey === OTHER_SERIES_LABEL || yKey.startsWith("_")) return null;
  const part = datePartLabellerFor(spec, dim);
  return { dimension: dim, values: [yKey], label: str(spec.labels?.[yKey] ?? (part ? part(yKey) : yKey)) };
}

function datumMatches(filter: DatumFilter, selected: Record<string, string[]>): boolean {
  const chosen = selected[filter.dimension];
  return !chosen || filter.values.some((v) => chosen.includes(v));
}

/** Is this datum inside the tile's own selection? With no selection on any
 *  of its dimensions everything is. */
export function isDatumSelected(datum: ChartDatum, selected: Record<string, string[]> | undefined): boolean {
  if (!selected || !Object.keys(selected).length) return true;
  const parts = datum.series ? [...datum.filters, datum.series] : datum.filters;
  return parts.every((p) => datumMatches(p, selected));
}

/** How much a data item outside the selection fades. An opacity, so the
 *  palette colour stays the tile's own in light and dark. */
export const UNSELECTED_OPACITY = 0.3;

function dimmed(item: unknown): unknown {
  if (item === null || item === undefined) return item;
  if (typeof item === "object" && !Array.isArray(item)) {
    const o = item as Record<string, any>;
    return { ...o, itemStyle: { ...(o.itemStyle ?? {}), opacity: UNSELECTED_OPACITY } };
  }
  return { value: item, itemStyle: { opacity: UNSELECTED_OPACITY } };
}

/**
 * Attach the click datums to every series `yKeyOf` names (the ones whose data
 * is aligned to `spec.rows` by index), and fade what lies outside the tile's
 * own selection. Series it does not name (gap columns, bands, markers) carry
 * nothing and stay as they are.
 */
export function attachChartDatums(
  option: EChartsOption,
  args: {
    spec: BackendChartSpec;
    interaction: DatumInteraction;
    timeIndex: boolean;
    /** The grain the time index is drawn at (period clicks only). */
    grain?: unknown;
    yKeyOf: (seriesIndex: number) => string | undefined;
  },
): EChartsOption {
  const { spec, interaction, timeIndex, grain, yKeyOf } = args;
  const series = (option as any).series;
  if (!Array.isArray(series)) return option;
  const rowDatums = chartRowDatums(spec, interaction, timeIndex);
  const rowPeriods = chartRowPeriods(spec, interaction, timeIndex, grain);
  const selected = interaction.selected ?? {};
  const selectedPeriod = interaction.selectedPeriod;
  const hasSelection = Object.keys(selected).length > 0 || !!selectedPeriod;
  const out = series.map((s: any, i: number) => {
    const yKey = yKeyOf(i);
    if (!s || yKey === undefined) return s;
    const sDatum = seriesDatum(spec, interaction, yKey);
    if (!sDatum && rowDatums.every((d) => d === null) && rowPeriods.every((p) => p === null)) return s;
    const next: any = { ...s, verityDatums: rowDatums, veritySeries: sDatum, verityPeriods: rowPeriods };
    if (hasSelection && Array.isArray(s.data)) {
      next.data = s.data.map((item: unknown, idx: number) => {
        const row = rowDatums[idx];
        const datum: ChartDatum = { filters: row ? [row] : [], ...(sDatum ? { series: sDatum } : {}) };
        const inPeriod = !selectedPeriod || rowPeriods[idx]?.start === selectedPeriod;
        return isDatumSelected(datum, selected) && inPeriod ? item : dimmed(item);
      });
    }
    return next;
  });
  return { ...option, series: out } as EChartsOption;
}

/** The datum behind an ECharts click, or null when that point selects
 *  nothing. `option` is the one the surface drew. */
export function datumFromClick(
  option: EChartsOption,
  params: { seriesIndex?: number; dataIndex?: number },
): ChartDatum | null {
  const series = (option as any)?.series;
  if (!Array.isArray(series) || params.seriesIndex == null || params.dataIndex == null) return null;
  const s = series[params.seriesIndex];
  if (!s) return null;
  const row: DatumFilter | null = s.verityDatums?.[params.dataIndex] ?? null;
  const sDatum: DatumFilter | null = s.veritySeries ?? null;
  const period: PeriodDatum | null = s.verityPeriods?.[params.dataIndex] ?? null;
  if (!row && !sDatum && !period) return null;
  return {
    filters: row ? [row] : [],
    ...(sDatum ? { series: sDatum } : {}),
    ...(period ? { period } : {}),
  };
}

/**
 * A pivot row's datum (plan E): a statement line hands on the accounts behind
 * it (`drill`); a plain row its whole path, parent values included, so
 * "Paid search › Brand" filters on that channel AND that campaign, never on
 * every "Brand". A NULL member (the "(empty)" row), the grand total and a
 * statement row without `drill` select nothing, as does any level whose
 * dimension the tile cannot be filtered on.
 */
export function pivotRowDatum(
  result: Pick<PivotResult, "row_dimensions" | "statement"> & { row_order?: PivotResult["row_order"] },
  row: Pick<PivotRow, "path" | "labels" | "drill">,
  clickable: readonly string[],
): ChartDatum | null {
  const allowed = new Set(clickable);
  if (row.drill) {
    const values = row.drill.values.filter((v) => v !== null && v !== undefined).map(String);
    if (!values.length || !allowed.has(row.drill.dimension)) return null;
    const label = row.labels[row.labels.length - 1] ?? values.join(", ");
    return { filters: [{ dimension: row.drill.dimension, values, label }] };
  }
  // A statement's path is line ids, not dimension values (both markers are
  // written together, `services/statements/pivot.py`; either is enough).
  if (result.statement || result.row_order === "statement") return null;
  if (!row.path.length || row.path.length > result.row_dimensions.length) return null;
  const filters: DatumFilter[] = [];
  for (let i = 0; i < row.path.length; i++) {
    const dimension = result.row_dimensions[i];
    const value = row.path[i];
    if (!dimension || value === null || value === undefined || !allowed.has(dimension)) return null;
    filters.push({ dimension, values: [String(value)], label: str(row.labels[i] ?? value) });
  }
  return { filters };
}
